/**
 * Payment debugging utilities for mobile devices
 */
import * as Sentry from '@sentry/react';

interface PaymentDebugInfo {
  userAgent: string;
  isIOS: boolean;
  isSafari: boolean;
  isHTTPS: boolean;
  applePay: {
    supported: boolean;
    canMakePayments: boolean;
    canMakePaymentsWithActiveCard: boolean;
  };
  stripeElements: {
    loaded: boolean;
    paymentElementMounted: boolean;
    addressElementMounted: boolean;
  };
  network: {
    online: boolean;
    connectionType: string;
  };
  errors: string[];
}

export const getPaymentDebugInfo = (): PaymentDebugInfo => {
  const userAgent = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  const isHTTPS = location.protocol === 'https:';

  const debugInfo: PaymentDebugInfo = {
    userAgent,
    isIOS,
    isSafari,
    isHTTPS,
    applePay: {
      supported: false,
      canMakePayments: false,
      canMakePaymentsWithActiveCard: false,
    },
    stripeElements: {
      loaded: !!window.Stripe,
      paymentElementMounted: !!document.querySelector('#payment-element'),
      addressElementMounted: !!document.querySelector('#address-element'),
    },
    network: {
      online: navigator.onLine,
      connectionType: (navigator as any).connection?.effectiveType || 'unknown',
    },
    errors: [],
  };

  // Check Apple Pay availability
  if (typeof window !== 'undefined' && (window as any).ApplePaySession) {
    try {
      debugInfo.applePay.supported = true;
      debugInfo.applePay.canMakePayments = (
        window as any
      ).ApplePaySession.canMakePayments();
      debugInfo.applePay.canMakePaymentsWithActiveCard = (
        window as any
      ).ApplePaySession.canMakePaymentsWithActiveCard(
        'merchant.your-merchant-id',
      );
    } catch (error) {
      debugInfo.errors.push(
        `Apple Pay check failed: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
    }
  }

  return debugInfo;
};

export const logPaymentDebugInfo = () => {
  const debugInfo = getPaymentDebugInfo();
  console.group('Payment Debug Info');
  console.log('Device Info:', {
    userAgent: debugInfo.userAgent,
    isIOS: debugInfo.isIOS,
    isSafari: debugInfo.isSafari,
    isHTTPS: debugInfo.isHTTPS,
  });
  console.log('Apple Pay:', debugInfo.applePay);
  console.log('Stripe Elements:', debugInfo.stripeElements);
  console.log('Network:', debugInfo.network);
  if (debugInfo.errors.length > 0) {
    console.error('Errors:', debugInfo.errors);
  }
  console.groupEnd();

  // Send debug info to Sentry for remote debugging
  Sentry.addBreadcrumb({
    message: 'Payment Debug Info Logged',
    category: 'payment.debug',
    level: 'info',
    data: debugInfo,
  });

  // If there are issues, capture them
  if (debugInfo.errors.length > 0) {
    Sentry.captureMessage('Payment Debug Issues Detected', {
      level: 'warning',
      tags: {
        payment_debug: true,
        device_type: debugInfo.isIOS ? 'ios' : 'other',
        browser_type: debugInfo.isSafari ? 'safari' : 'other',
      },
      extra: {
        ...debugInfo,
        debugTimestamp: new Date().toISOString(),
      },
    });
  }

  return debugInfo;
};

export const validatePaymentRequirements = (): {
  isValid: boolean;
  issues: string[];
} => {
  const issues: string[] = [];
  const debugInfo = getPaymentDebugInfo();

  if (process.env.REACT_APP_NODE_ENV !== 'development' && !debugInfo.isHTTPS) {
    issues.push('HTTPS is required for payment processing');
  }

  if (!debugInfo.stripeElements.loaded) {
    issues.push('Stripe JavaScript library not loaded');
  }

  if (!debugInfo.network.online) {
    issues.push('Device is offline');
  }

  if (debugInfo.isIOS && !debugInfo.isSafari) {
    issues.push(
      'Apple Pay may not work properly in non-Safari browsers on iOS',
    );
  }

  return {
    isValid: issues.length === 0,
    issues,
  };
};

// Auto-log debug info in development
if (process.env.NODE_ENV === 'development') {
  window.addEventListener('load', () => {
    setTimeout(logPaymentDebugInfo, 1000);
  });
}
